// Payment Gateway Configuration

export type PaymentGateway = 'razorpay' | 'stripe' | 'dodo';

export interface PaymentGatewayConfig {
  razorpay: {
    keyId: string;
    enabled: boolean;
    preferredCountries: string[];
  };
  stripe: {
    publishableKey: string;
    enabled: boolean;
    preferredCountries: string[];
  };
}

// Get Razorpay key from environment
export const getRazorpayConfig = () => {
  const keyId = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
  return {
    keyId: keyId,
    enabled: !!keyId,
  };
};

// Get Stripe key from environment
export const getStripeConfig = () => {
  return {
    publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
    enabled: !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  };
};

function parseEnvBool(value: string | undefined): boolean {
  if (value === undefined || value === null) return false;
  const v = String(value).trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

/** Opt-in: show Razorpay in hosted checkout for India (requires REACT_APP_RAZORPAY_KEY_ID). */
function isRazorpayCheckoutExplicitlyEnabled(): boolean {
  return parseEnvBool(process.env.REACT_APP_RAZORPAY_CHECKOUT_ENABLED);
}

// Dodo Payments: secret API key lives only on Firebase Functions (DODO_PAYMENTS_API_KEY).
// These flags are optional metadata / future use; checkout UI defaults to Dodo unless
// REACT_APP_RAZORPAY_CHECKOUT_ENABLED opts India back into Razorpay.
export const getDodoConfig = () => {
  const enabled =
    parseEnvBool(process.env.REACT_APP_DODO_CHECKOUT_ENABLED) ||
    parseEnvBool(process.env.REACT_APP_CHECKOUT_USE_DODO_ONLY);
  return {
    enabled,
  };
};

/**
 * When true, Razorpay must not appear or load in checkout UIs — only Dodo hosted checkout is offered.
 * Default: true (Dodo for all regions). Set REACT_APP_RAZORPAY_CHECKOUT_ENABLED=true to offer Razorpay for India.
 */
export const isRazorpayCheckoutSuppressed = (): boolean => !isRazorpayCheckoutExplicitlyEnabled();

/**
 * Which payment gateway(s) checkout may use.
 * Default: Dodo for every country (including India). Razorpay appears only when explicitly enabled for India.
 */
export const getAvailablePaymentGateways = (country: string | null): PaymentGateway[] => {
  const razorpayConfig = getRazorpayConfig();
  const isIndia = country === 'India' || country === 'IN';

  if (isRazorpayCheckoutExplicitlyEnabled() && isIndia && razorpayConfig.enabled) {
    return ['razorpay'];
  }

  return ['dodo'];
};
